import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  NgDialogModule,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IBridgeDevice,
  ROMEXIS_BRIDGE_CONFIGURATION_OPTIONS,
  RomexisBridgeConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import { asyncForEach, Firestore, WithRef } from '@principle-theorem/shared';

export interface IRomexisBridgeConfigurationData {
  bridgeDevices: WithRef<IBridgeDevice>[];
}

interface IRomexisBridgeConfigurationForm {
  device: WithRef<IBridgeDevice>;
  configuration: RomexisBridgeConfiguration;
}

@Component({
  selector: 'pr-romexis-bridge-configuration',
  imports: [NgDialogModule, InformationBoxComponent, MatTooltip, MatDivider],
  templateUrl: './romexis-bridge-configuration.component.html',
  styleUrl: './romexis-bridge-configuration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RomexisBridgeConfigurationComponent {
  private _snackBar = inject(MatSnackBar);
  private _dialogRef = inject(MatDialogRef);
  data = inject<IRomexisBridgeConfigurationData>(MAT_DIALOG_DATA);
  form = new TypedFormArray<IRomexisBridgeConfigurationForm>([]);
  configOptions = ROMEXIS_BRIDGE_CONFIGURATION_OPTIONS;

  constructor() {
    this.data.bridgeDevices.map((device) =>
      this.form.push(this._buildConfigurationForm(device))
    );
  }

  get formArray(): TypedFormGroup<IRomexisBridgeConfigurationForm>[] {
    return this.form
      .controls as TypedFormGroup<IRomexisBridgeConfigurationForm>[];
  }

  getConfigurationLabel(configuration: RomexisBridgeConfiguration): string {
    return (
      this.configOptions.find((option) => option.type === configuration)
        ?.label ?? 'Select Configuration'
    );
  }

  applyToAll(configuration: RomexisBridgeConfiguration): void {
    this.formArray.map((form) => {
      form.controls.configuration.setValue(configuration);
      form.controls.configuration.markAsDirty();
    });
  }

  async save(): Promise<void> {
    await asyncForEach(this.formArray, async (form) => {
      const data = form.getRawValue();
      if (!data.device.settings?.romexis) {
        return;
      }
      await Firestore.patchDoc(data.device.ref, {
        settings: {
          ...data.device.settings,
          romexis: {
            ...data.device.settings.romexis,
            configuration: data.configuration,
          },
        },
      });
    });

    this._snackBar.open('Romexis Bridge Settings Saved');
    this._dialogRef.close();
  }

  private _buildConfigurationForm(
    device: WithRef<IBridgeDevice>
  ): TypedFormGroup<IRomexisBridgeConfigurationForm> {
    return new TypedFormGroup<IRomexisBridgeConfigurationForm>({
      device: new TypedFormControl<WithRef<IBridgeDevice>>(device),
      configuration: new TypedFormControl<RomexisBridgeConfiguration>(
        device.settings?.romexis?.configuration ?? RomexisBridgeConfiguration.V5
      ),
    });
  }
}
